import { zodResolver } from "@hookform/resolvers/zod";
import { Train } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "wouter";

import { loginSchema, type LoginCredentials } from "./model";

import {
  Button,
  Checkbox,
  ClassicLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  LoadingModal,
} from "@/common/components";
import { ROUTES } from "@/common/config";
import { useAuth, useToast } from "@/common/hooks";

export function LoginPage() {
  const { login, query } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const [checked, setChecked] = useState(false);

  const form = useForm<Omit<LoginCredentials, "role">>({
    resolver: zodResolver(loginSchema),
    mode: "onChange",
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const onSubmit = ({ username, password }: LoginCredentials) => {
    login({ login: username.trim(), password: password.trim(), remember: checked })
      .then(() => {
        toast({
          title: "Успешный вход",
          description: "Добро пожаловать в систему!",
        });

        setLocation(ROUTES.ROOT);
      })
      .catch((error) => {
        toast({
          variant: "destructive",
          title: "Ошибка входа",
          description: error instanceof Error ? error.message : null,
        });
      });
  };

  return (
    <>
      <div
        className="h-full min-h-[inherit] flex items-center justify-center pt-28 pb-6 md:py-0"
        style={{
          backgroundImage:
            "linear-gradient(-45deg, rgb(251,229,214) 0%, rgb(255,255,255) 50%, rgb(251,229,214) 100%)",
        }}
      >
        <div className="bg-white md:py-7 md:px-8 p-6 rounded-lg shadow-lg w-full md:max-w-[440px] max-w-[361px] md:m-[72px]">
          <div className="text-center md:mb-8 mb-7">
            <h1
              className="text-2xl font-semibold leading-7 hidden md:block "
              style={{ color: "var(--color-dasha-style)" }}
            >
              Электронная Информационная Система
            </h1>
            <div
              className="text-2xl font-semibold leading-7 block md:hidden "
              style={{ color: "var(--color-dasha-style)" }}
            >
              <div>Электронная</div>
              <div>Информационная</div>
              <div>Система</div>
            </div>
            <p className="text-sm font-normal leading-5 md:mt-4 mt-3">
              Онлайн сервис и цифровая система для железнодорожных перевозок
            </p>
          </div>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="md:space-y-5 space-y-3"
              autoComplete="off"
              noValidate
            >
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Логин или email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Введите логин или email"
                        {...field}
                        autoFocus
                        autoComplete="username-off"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Пароль</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Введите пароль"
                        {...field}
                        autoComplete="new-password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Запомнить пароль */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember"
                  checked={checked}
                  onCheckedChange={() => setChecked(!checked)}
                />
                <label
                  htmlFor="remember"
                  className="text-xs font-semibold leading-4 cursor-pointer"
                >
                  Запомнить меня
                </label>
              </div>

              <div className="text-Text-secondary text-xs leading-4">
                Нажимая кнопку “Войти в систему” вы принимаете{" "}
                <ClassicLink href="/user_agreement.pdf">пользовательское соглашение</ClassicLink> и{" "}
                <ClassicLink href="/privacy_policy.pdf">политику конфиденциальности</ClassicLink>
              </div>

              <Button type="submit" className="w-full btn-primary" disabled={query.isPending}>
                Войти в систему
              </Button>
            </form>
          </Form>

          {/* Ссылка на восстановление пароля */}
          {/* <div className="mt-4 text-center">
            <ResetPasswordDialog isOpen={isResetDialogOpen} setIsOpen={setIsResetDialogOpen} />
          </div> */}
        </div>
      </div>

      <LoadingModal isOpen={query.isPending} />
    </>
  );
}
